import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MdRefresh,
  MdSearch,
  MdInventory2,
  MdWarning,
  MdCancel,
  MdFilterList,
  MdClose,
  MdCheckCircle,
} from 'react-icons/md';
import { Link } from 'react-router-dom';
import { PageHeader } from '@euroasiann/shared/ui';
import { authenticatedFetch } from '../../lib/api';
import { useToast } from '../../components/shared/Toast';
import { useCurrency } from '../../context/CurrencyContext';

interface InventoryPart {
  _id: string;
  partNumber: string;
  name?: string;
  priceUSD?: number;
  stockQuantity?: number;
  position?: string;
  drawing?: string;
  dimensions?: string;
  parentCategoryName?: string;
  parentSubCategoryName?: string;
  parentBrandName?: string;
  parentModelName?: string;
}

/* ─── Skeleton row ────────────────────────────────────────── */
function SkeletonRow() {
  return (
    <tr className="border-b border-[hsl(var(--border))]/40 animate-pulse">
      {Array.from({ length: 10 }).map((_, i) => (
        <td key={i} className="px-5 py-4">
          <div className="h-3.5 rounded-full bg-[hsl(var(--muted))]/60" style={{ width: `${55 + (i * 17) % 40}%` }} />
        </td>
      ))}
    </tr>
  );
}

/* ─── Stat card ───────────────────────────────────────────── */
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  iconBg: string;
  iconColor: string;
  borderColor: string;
}
function StatCard({ icon, label, value, iconBg, iconColor, borderColor }: StatCardProps) {
  return (
    <div className={`bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] border-l-[3px] ${borderColor} px-4 py-3 flex items-center gap-3`}>
      <div className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${iconBg}`}>
        <span className={`text-base ${iconColor}`}>{icon}</span>
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#0088cc] leading-none mb-1">{label}</p>
        <p className="text-lg font-bold text-[hsl(var(--foreground))] leading-none truncate">{value}</p>
      </div>
    </div>
  );
}

/* ─── Stock badge ─────────────────────────────────────────── */
function StockBadge({ qty }: { qty: number | undefined }) {
  const q = qty ?? 0;
  if (q > 5)
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 ring-1 ring-emerald-500/10">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
        {q} in stock
      </span>
    );
  if (q > 0)
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-300 border border-amber-500/25 ring-1 ring-amber-500/10">
        <MdWarning className="w-3 h-3 flex-shrink-0" />
        {q} low stock
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/25 ring-1 ring-red-500/10">
      <MdCancel className="w-3 h-3 flex-shrink-0" />
      Out of stock
    </span>
  );
}

/* ─── Category pill ───────────────────────────────────────── */
function CategoryPill({ name }: { name: string }) {
  return (
    <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-semibold bg-[#0088cc]/10 text-[#38bdf8] border border-[#0088cc]/20">
      {name}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════ */
export function InventoryPage() {
  const { showToast } = useToast();
  const { formatAmount } = useCurrency();
  const [searchQuery, setSearchQuery] = useState('');
  const [stockFilter, setStockFilter] = useState<'all' | 'in' | 'low' | 'out'>('all');

  // React Query — caches result for 2 min, no refetch on window focus
  const { data: parts = [], isFetching, isLoading, refetch } = useQuery<InventoryPart[]>({
    queryKey: ['admin-inventory'],
    queryFn: async () => {
      const res = await authenticatedFetch('/api/v1/admin/inventory');
      if (!res.ok) throw new Error('Failed to load inventory');
      const json = await res.json();
      return Array.isArray(json.data) ? json.data : [];
    },
    staleTime: 2 * 60 * 1000,      // treat data as fresh for 2 minutes
    gcTime: 5 * 60 * 1000,         // keep in cache for 5 minutes
    refetchOnWindowFocus: false,
    throwOnError: false,
    meta: {
      onError: (err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to load inventory';
        showToast(message, 'error');
      },
    },
  });

  const loading = isLoading;

  /* ── derived stats ── */
  const stats = useMemo(() => {
    const totalItems  = parts.length;
    const inStock     = parts.filter((p) => (p.stockQuantity ?? 0) > 5).length;
    const lowStock    = parts.filter((p) => (p.stockQuantity ?? 0) > 0 && (p.stockQuantity ?? 0) <= 5).length;
    const outOfStock  = parts.filter((p) => (p.stockQuantity ?? 0) === 0).length;
    return { totalItems, inStock, lowStock, outOfStock };
  }, [parts]);

  /* ── filtered parts ── */
  const filteredParts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return parts.filter((part) => {
      if (stockFilter === 'in' && (part.stockQuantity ?? 0) <= 5) return false;
      if (stockFilter === 'low' && ((part.stockQuantity ?? 0) === 0 || (part.stockQuantity ?? 0) > 5)) return false;
      if (stockFilter === 'out' && (part.stockQuantity ?? 0) !== 0) return false;
      if (!q) return true;
      return [
        part.partNumber, part.name, part.parentCategoryName,
        part.parentSubCategoryName, part.parentBrandName,
        part.parentModelName, part.position, part.drawing,
      ].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [parts, searchQuery, stockFilter]);

  /* ─────────────────────────────── JSX ─────────────────────────────── */
  return (
    <div className="w-full min-h-screen p-4 lg:p-8 bg-[hsl(var(--background))] font-sans text-[hsl(var(--foreground))]">
      <PageHeader
        title="Inventory"
        subtitle="Real-time view of all in-stock catalog parts. Zero-stock parts stay in the Parts Catalog and appear here once stock is added."
        subtitleVariant="description"
        subtitleAccent={false}
        breadcrumb={[
          { label: 'Admin' },
          { label: 'Inventory', current: true },
        ]}
        actions={
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-xl bg-[#0088cc] px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-[#0088cc]/25 transition-colors hover:bg-[#0077b3] disabled:opacity-50"
          >
            <MdRefresh className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      <p className="mb-6 mt-4 text-sm text-[hsl(var(--muted-foreground))]">
        Browse the{' '}
        <Link
          to="/dashboard/admin/product-hierarchy"
          className="font-semibold text-[#0088cc] hover:text-[#0077b3] hover:underline"
        >
          Parts Catalog
        </Link>{' '}
        for the full hierarchy.
      </p>

      <div className="space-y-6">

        {/* ── Summary stat cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={<MdInventory2 />}
            label="Total Parts"
            value={loading ? '—' : stats.totalItems.toLocaleString()}
            iconBg="bg-[#0088cc]/10"
            iconColor="text-[#38bdf8]"
            borderColor="border-l-[#0088cc]"
          />
          <StatCard
            icon={<MdCheckCircle />}
            label="In Stock"
            value={loading ? '—' : stats.inStock.toLocaleString()}
            iconBg="bg-emerald-500/10"
            iconColor="text-emerald-400"
            borderColor="border-l-emerald-500"
          />
          <StatCard
            icon={<MdWarning />}
            label="Low Stock"
            value={loading ? '—' : stats.lowStock.toLocaleString()}
            iconBg="bg-amber-500/10"
            iconColor="text-amber-400"
            borderColor="border-l-amber-500"
          />
          <StatCard
            icon={<MdCancel />}
            label="Out of Stock"
            value={loading ? '—' : stats.outOfStock.toLocaleString()}
            iconBg="bg-red-500/10"
            iconColor="text-red-400"
            borderColor="border-l-red-500"
          />
        </div>

        {/* ── Table card ── */}
        <div className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))] overflow-hidden shadow-sm">

          {/* toolbar */}
          <div className="px-5 py-4 border-b border-[hsl(var(--border))]/60 flex flex-col sm:flex-row sm:items-center gap-3">

            {/* search */}
            <div className="relative flex-1 max-w-sm">
              <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-[hsl(var(--muted-foreground))] pointer-events-none" />
              <input
                type="search"
                placeholder="Search part, brand, model…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-9 py-2.5 bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-xl text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] outline-none focus:ring-2 focus:ring-[#0088cc]/30 focus:border-[#0088cc]/60 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                >
                  <MdClose className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* stock filter pills */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <MdFilterList className="w-4 h-4 text-[hsl(var(--muted-foreground))] flex-shrink-0" />
              {(['all', 'in', 'low', 'out'] as const).map((f) => {
                const labels = { all: 'All', in: 'In Stock', low: 'Low Stock', out: 'Out of Stock' };
                const active = stockFilter === f;
                return (
                  <button
                    key={f}
                    onClick={() => setStockFilter(f)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                      active
                        ? 'bg-[#0088cc] border-[#0088cc] text-white shadow-sm'
                        : 'border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[hsl(var(--foreground))]/30'
                    }`}
                  >
                    {labels[f]}
                  </button>
                );
              })}
            </div>

            {/* count badge */}
            <span className="ml-auto flex-shrink-0 text-xs font-semibold text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]/40 px-3 py-1.5 rounded-full border border-[hsl(var(--border))]/60">
              {filteredParts.length.toLocaleString()} item{filteredParts.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* table */}
          {loading ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px]">
                <thead>
                  <tr className="border-b border-[#0088cc]/20 bg-[#e8f4fb] dark:bg-[#0f2744]">
                    {['Category', 'Subcategory', 'Brand', 'Model', 'Part Number', 'Position', 'Drawing', 'Dimensions', 'Price', 'Stock'].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-[#0088cc]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 7 }).map((_, i) => <SkeletonRow key={i} />)}
                </tbody>
              </table>
            </div>
          ) : filteredParts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--muted))]/40 flex items-center justify-center mb-4">
                <MdInventory2 className="w-8 h-8 text-[hsl(var(--muted-foreground))]" />
              </div>
              <p className="text-base font-semibold text-[hsl(var(--foreground))] mb-1">
                {parts.length === 0 ? 'No inventory yet' : 'No results found'}
              </p>
              <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-sm">
                {parts.length === 0
                  ? 'Add stock to a catalog part to list it here.'
                  : 'Try adjusting your search or filter.'}
              </p>
              {parts.length === 0 && (
                <Link
                  to="/dashboard/admin/product-hierarchy"
                  className="mt-5 inline-flex items-center px-5 py-2.5 rounded-xl bg-[#0088cc] hover:bg-[#0077b3] text-white text-sm font-bold transition-colors"
                >
                  Open Parts Catalog
                </Link>
              )}
              {parts.length > 0 && (searchQuery || stockFilter !== 'all') && (
                <button
                  onClick={() => { setSearchQuery(''); setStockFilter('all'); }}
                  className="mt-4 text-sm text-[#38bdf8] hover:underline font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] border-collapse">
                <thead>
                  <tr className="sticky top-0 z-10 border-b border-[#0088cc]/20 bg-[#e8f4fb] dark:bg-[#0f2744]">
                    {[
                      'Category', 'Subcategory', 'Brand', 'Model',
                      'Part Number', 'Position', 'Drawing', 'Dimensions',
                      'Price', 'Stock',
                    ].map((h) => (
                      <th
                        key={h}
                        className="whitespace-nowrap px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-[#0088cc]"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredParts.map((part, idx) => (
                    <tr
                      key={part._id}
                      className={`border-b border-[hsl(var(--border))]/40 transition-colors hover:bg-[#0088cc]/5 ${
                        idx % 2 === 0 ? 'bg-transparent' : 'bg-[hsl(var(--muted))]/10'
                      }`}
                    >
                      {/* Category */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        {part.parentCategoryName
                          ? <CategoryPill name={part.parentCategoryName} />
                          : <span className="text-[hsl(var(--muted-foreground))] text-sm">—</span>}
                      </td>

                      {/* Subcategory */}
                      <td className="px-5 py-3.5 text-sm text-[hsl(var(--muted-foreground))] max-w-[160px] truncate">
                        {part.parentSubCategoryName || '—'}
                      </td>

                      {/* Brand */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-bold text-[hsl(var(--foreground))]">
                          {part.parentBrandName || '—'}
                        </span>
                      </td>

                      {/* Model */}
                      <td className="px-5 py-3.5 text-sm text-[hsl(var(--muted-foreground))] max-w-[140px] truncate">
                        {part.parentModelName || '—'}
                      </td>

                      {/* Part Number */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span className="font-mono text-sm font-bold text-[#38bdf8] bg-[#0088cc]/8 px-2 py-0.5 rounded-md border border-[#0088cc]/20">
                          {part.partNumber || '—'}
                        </span>
                      </td>

                      {/* Position */}
                      <td className="px-5 py-3.5 text-sm text-[hsl(var(--muted-foreground))]">
                        {part.position || '—'}
                      </td>

                      {/* Drawing */}
                      <td className="px-5 py-3.5 text-sm text-[hsl(var(--muted-foreground))]">
                        {part.drawing || '—'}
                      </td>

                      {/* Dimensions */}
                      <td className="px-5 py-3.5 text-sm text-[hsl(var(--muted-foreground))]">
                        {part.dimensions || '—'}
                      </td>

                      {/* Price */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-extrabold text-[hsl(var(--foreground))]">
                          {formatAmount(part.priceUSD || 0)}
                        </span>
                      </td>

                      {/* Stock */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <StockBadge qty={part.stockQuantity} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* footer bar */}
          {!loading && filteredParts.length > 0 && (
            <div className="px-5 py-3 border-t border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/10 flex items-center justify-between">
              <span className="text-xs text-[hsl(var(--muted-foreground))]">
                Showing <span className="font-semibold text-[hsl(var(--foreground))]">{filteredParts.length}</span> of <span className="font-semibold text-[hsl(var(--foreground))]">{parts.length}</span> parts
              </span>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">
                Total stock value:{' '}
                <span className="font-bold text-emerald-400">
                  {formatAmount(filteredParts.reduce((s, p) => s + (p.priceUSD || 0) * (p.stockQuantity || 0), 0))}
                </span>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
